/**
 * Contact validation for sign-in (website and phone app). Kept dependency-free: a full phone metadata library is
 * hundreds of KB, and the SMS provider does the authoritative check. This catches typos and
 * obviously invalid input before we spend an SMS or an email.
 */

export interface Country {
  iso: string;
  name: string;
  dial: string;
  /** Allowed national number lengths (digits after the country code, no trunk 0). */
  lengths: number[];
}

export const COUNTRIES: Country[] = [
  { iso: 'US', name: 'United States', dial: '1', lengths: [10] },
  { iso: 'CA', name: 'Canada', dial: '1', lengths: [10] },
  { iso: 'GB', name: 'United Kingdom', dial: '44', lengths: [10] },
  { iso: 'IE', name: 'Ireland', dial: '353', lengths: [9] },
  { iso: 'AU', name: 'Australia', dial: '61', lengths: [9] },
  { iso: 'NZ', name: 'New Zealand', dial: '64', lengths: [8, 9, 10] },
  { iso: 'DE', name: 'Germany', dial: '49', lengths: [10, 11] },
  { iso: 'FR', name: 'France', dial: '33', lengths: [9] },
  { iso: 'ES', name: 'Spain', dial: '34', lengths: [9] },
  { iso: 'IT', name: 'Italy', dial: '39', lengths: [9, 10] },
  { iso: 'NL', name: 'Netherlands', dial: '31', lengths: [9] },
  { iso: 'SE', name: 'Sweden', dial: '46', lengths: [7, 8, 9] },
  { iso: 'BR', name: 'Brazil', dial: '55', lengths: [10, 11] },
  { iso: 'MX', name: 'Mexico', dial: '52', lengths: [10] },
  { iso: 'IN', name: 'India', dial: '91', lengths: [10] },
  { iso: 'JP', name: 'Japan', dial: '81', lengths: [9, 10] },
  { iso: 'KR', name: 'South Korea', dial: '82', lengths: [9, 10] },
  { iso: 'PH', name: 'Philippines', dial: '63', lengths: [10] },
  { iso: 'SG', name: 'Singapore', dial: '65', lengths: [8] },
  { iso: 'ZA', name: 'South Africa', dial: '27', lengths: [9] },
  { iso: 'NG', name: 'Nigeria', dial: '234', lengths: [10] },
];

/** 🇺🇸 from US: each letter maps to its regional indicator symbol. */
export function flag(iso: string): string {
  return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Best guess from the browser's or phone's languages, e.g. en-GB → GB. Falls back to US. */
export function guessCountry(locales: readonly string[]): Country {
  for (const lang of locales) {
    const region = lang.split('-')[1]?.toUpperCase();
    const hit = COUNTRIES.find((c) => c.iso === region);
    if (hit) return hit;
  }
  return COUNTRIES[0]!;
}

/** Digits only, without a leading trunk 0 (UK 07700… → 7700…) or a pasted country code. */
export function nationalDigits(input: string, country: Country): string {
  let d = input.replace(/\D/g, '');
  if (input.trim().startsWith('+') && d.startsWith(country.dial)) d = d.slice(country.dial.length);
  else if (country.dial === '1' && d.length === 11 && d.startsWith('1')) d = d.slice(1);
  if (country.dial !== '1' && d.startsWith('0')) d = d.slice(1);
  return d;
}

export type PhoneCheck = { ok: true; e164: string } | { ok: false; message: string };

export function checkPhone(input: string, country: Country): PhoneCheck {
  if (/[a-z]/i.test(input)) return { ok: false, message: 'Use numbers only.' };
  const d = nationalDigits(input, country);
  if (!d) return { ok: false, message: 'Enter your mobile number.' };
  if (!country.lengths.includes(d.length)) {
    const want = country.lengths.join(' or ');
    return { ok: false, message: `${country.name} mobile numbers have ${want} digits after +${country.dial}.` };
  }
  // North American numbers: area code and exchange can't start with 0 or 1.
  if (country.dial === '1' && (/^[01]/.test(d) || /^...[01]/.test(d))) return { ok: false, message: 'That doesn’t look like a valid number.' };
  return { ok: true, e164: `+${country.dial}${d}` };
}

/** Pretty-prints as the user types: 5551234567 → 555 123 4567 (US/CA), otherwise groups of 3–4. */
export function formatNational(digits: string, country: Country): string {
  if (country.dial === '1') return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)].filter(Boolean).join(' ');
  return digits.replace(/(\d{3,4})(?=\d{3})/g, '$1 ').trim();
}

/** Catches the obvious: missing @, missing domain, spaces, no dot in the domain. */
export function checkEmail(input: string): { ok: true; email: string } | { ok: false; message: string } {
  const email = input.trim();
  if (!email) return { ok: false, message: 'Enter your email.' };
  if (!email.includes('@')) return { ok: false, message: 'Your email needs an @, like you@example.com.' };
  if (/\s/.test(email)) return { ok: false, message: 'Emails can’t contain spaces.' };
  if (!/^[^@]+@[^@]+\.[a-z]{2,}$/i.test(email) || /\.\.|@\.|\.@/.test(email) || email.length > 254) {
    return { ok: false, message: 'That doesn’t look like a valid email.' };
  }
  return { ok: true, email };
}
